import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BookOpen, Calendar, Check, ClipboardList, Settings, ShoppingBag, Sparkles, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { OnboardingStatus } from '../../api/OnboardingAPI';
import CONFIG from '../../config/config';
import { useOnboarding } from '../../hooks/useOnboarding';
import { cn } from '../../lib/utils';
import { ButtonV2 } from '../ButtonV2/ButtonV2';

type StepId = keyof OnboardingStatus['status'];

type ChecklistStep = {
  id: StepId;
  titleKey: string;
  descriptionKey: string;
  href: string;
  goAction: string;
  skipAction: string;
  Icon: LucideIcon;
};

const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    id: 'setupStore',
    titleKey: 'onboarding.menu.store',
    descriptionKey: 'onboarding.menu.store.description',
    href: '/settings',
    goAction: 'Onboarding Settings',
    skipAction: 'Complete Onboarding Settings',
    Icon: Settings,
  },
  {
    id: 'startCalendar',
    titleKey: 'onboarding.menu.calendar',
    descriptionKey: 'onboarding.menu.calendar.description',
    href: '/onboarding/booking',
    goAction: 'Onboarding Calendar',
    skipAction: 'Complete Onboarding Calendar',
    Icon: Calendar,
  },
  {
    id: 'publishCatalog',
    titleKey: 'onboarding.menu.catalog',
    descriptionKey: 'onboarding.menu.catalog.description',
    href: '/onboarding/catalog',
    goAction: 'Onboarding Catalogues',
    skipAction: 'Complete Onboarding Catalogues',
    Icon: BookOpen,
  },
  {
    id: 'setupDrop',
    titleKey: 'onboarding.menu.drop',
    descriptionKey: 'onboarding.menu.drop.description',
    href: '/onboarding/drop',
    goAction: 'Onboarding Drop',
    skipAction: 'Complete Onboarding Drop',
    Icon: ClipboardList,
  },
  {
    id: 'registerOrder',
    titleKey: 'onboarding.menu.order',
    descriptionKey: 'onboarding.menu.order.description',
    href: '/orders',
    goAction: 'Onboarding Orders',
    skipAction: 'Complete Onboarding Orders',
    Icon: ShoppingBag,
  },
];

const restoreBodyScroll = () => {
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
};

type StepRowProps = {
  step: ChecklistStep;
  completed: boolean;
  prefersReducedMotion: boolean | null;
  onGo: (href: string) => void;
  onSkip: (id: StepId) => void;
};

function StepRow({ step, completed, prefersReducedMotion, onGo, onSkip }: StepRowProps) {
  const { t } = useTranslation();
  const { Icon } = step;

  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl p-3 transition-colors',
        completed ? 'bg-muted' : 'bg-as-pink-100 dark:bg-as-pink-100/90'
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          completed
            ? 'bg-as-pink-400 text-gray-900'
            : 'border border-as-pink-300 bg-white/80 text-as-pink-500 dark:bg-white/10'
        )}
      >
        {completed ? (
          <motion.span
            className="flex"
            initial={prefersReducedMotion ? false : { scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 420, damping: 22 }}
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </motion.span>
        ) : (
          <Icon className="h-4 w-4" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-semibold text-foreground', completed && 'text-muted-foreground')}>
          {t(step.titleKey)}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t(step.descriptionKey)}</p>
        {!completed && (
          <div className="mt-3 flex flex-wrap items-center justify-end gap-1">
            <ButtonV2
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-muted-foreground"
              actionName={step.skipAction}
              onClick={() => onSkip(step.id)}
            >
              {t('global.mark_completed')}
            </ButtonV2>
            <ButtonV2
              type="button"
              size="sm"
              className="h-8 bg-as-pink-400 px-3 text-xs text-gray-900 hover:bg-as-pink-500"
              actionName={step.goAction}
              onClick={() => onGo(step.href)}
            >
              {t('global.go')}
            </ButtonV2>
          </div>
        )}
      </div>
    </div>
  );
}

type ChecklistPanelProps = {
  title: string;
  showHeading?: boolean;
  completedCount: number;
  total: number;
  status: OnboardingStatus['status'] | undefined;
  prefersReducedMotion: boolean | null;
  onGo: (href: string) => void;
  onSkip: (id: StepId) => void;
};

function ChecklistPanel({
  title,
  showHeading = true,
  completedCount,
  total,
  status,
  prefersReducedMotion,
  onGo,
  onSkip,
}: ChecklistPanelProps) {
  const { t } = useTranslation();
  const progressLabel = t('onboarding.menu.progress', { done: completedCount, total });
  const progressPercent = total === 0 ? 0 : (completedCount / total) * 100;

  return (
    <>
      <div className="border-b border-as-pink-200/50 px-4 py-3">
        <div className={cn('flex items-center gap-3', showHeading ? 'justify-between' : 'justify-end')}>
          {showHeading ? <h3 className="text-sm font-semibold text-foreground">{title}</h3> : null}
          <span className="rounded-full bg-as-pink-100 px-2 py-0.5 text-[11px] font-semibold text-as-pink-500 dark:bg-as-pink-400 dark:text-black">
            {progressLabel}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-as-pink-100">
          <motion.div
            className="h-full rounded-full bg-as-pink-400"
            initial={false}
            animate={{ width: `${progressPercent}%` }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.35 }}
          />
        </div>
      </div>
      <motion.ul
        className="space-y-2 p-3"
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: {
            transition: { staggerChildren: prefersReducedMotion ? 0 : 0.05 },
          },
        }}
      >
        {CHECKLIST_STEPS.map((step) => (
          <motion.li
            key={step.id}
            variants={{
              hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 8 },
              show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
            }}
          >
            <StepRow
              step={step}
              completed={Boolean(status?.[step.id])}
              prefersReducedMotion={prefersReducedMotion}
              onGo={onGo}
              onSkip={onSkip}
            />
          </motion.li>
        ))}
      </motion.ul>
    </>
  );
}

export function OnboardingChecklist() {
  const { t } = useTranslation();
  const isLoggedIn = useSelector((state: { settings: { value: { isLoggedIn: boolean } } }) =>
    state.settings.value.isLoggedIn
  );
  const navigate = useNavigate();
  const { onboardingStatus, skipStep } = useOnboarding(isLoggedIn);
  const prefersReducedMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const status = onboardingStatus?.status;
  const completedCount = CHECKLIST_STEPS.filter((step) => Boolean(status?.[step.id])).length;
  const total = CHECKLIST_STEPS.length;
  const allStepsCompleted = Boolean(status) && completedCount === total;

  const closeMenu = () => {
    setIsOpen(false);
    restoreBodyScroll();
  };

  const toggleMenu = () => {
    const isMobile = window.innerWidth < CONFIG.MOBILE_BREAKPOINT;
    if (isOpen) {
      closeMenu();
      return;
    }
    setIsOpen(true);
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => restoreBodyScroll();
  }, []);

  if (!isLoggedIn || allStepsCompleted) {
    return null;
  }

  const title = t('onboarding.menu');
  const progressLabel = t('onboarding.menu.progress', { done: completedCount, total });
  const panelMotion = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: -8, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -8, scale: 0.98 },
        transition: { duration: 0.18 },
      };

  const handleGo = (href: string) => {
    closeMenu();
    navigate(href);
  };

  return (
    <div className="eateliers-brand relative" ref={dropdownRef}>
      <motion.button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`${title} ${progressLabel}`}
        onClick={toggleMenu}
        data-dd-action-name="Onboarding Menu"
        className={cn(
          'onboarding-shimmer inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-as-pink-300 via-as-pink-400 to-as-pink-500 px-2.5 py-1.5 text-black shadow-lg',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-as-pink-400 focus-visible:ring-offset-2'
        )}
        whileHover={prefersReducedMotion ? undefined : { scale: 1.03 }}
        whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
      >
        <Sparkles className="relative z-10 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="relative z-10 hidden text-sm font-semibold tracking-tight md:inline">{title}</span>
        <span className="relative z-10 text-sm font-semibold tracking-tight md:hidden">
          {t('onboarding.menu.mobile')}
        </span>
        <span className="relative z-10 rounded-full bg-white/90 px-1.5 py-0.5 text-[10px] font-bold leading-none text-black">
          {progressLabel}
        </span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
            />
            <motion.nav
              aria-label={title}
              className="fixed inset-0 z-50 flex flex-col bg-card text-foreground md:hidden"
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
            >
              <div className="flex items-center justify-between border-b border-as-pink-200/50 p-4">
                <h2 className="text-lg font-semibold">{title}</h2>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="rounded-lg p-2 text-foreground hover:bg-muted"
                  aria-label={t('global.close') as string}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <ChecklistPanel
                  title={title}
                  showHeading={false}
                  completedCount={completedCount}
                  total={total}
                  status={status}
                  prefersReducedMotion={prefersReducedMotion}
                  onGo={handleGo}
                  onSkip={(id) => { void skipStep(id); }}
                />
              </div>
            </motion.nav>
            <motion.nav
              aria-label={title}
              className="absolute right-0 top-full z-50 mt-2 hidden w-[22rem] max-w-[90vw] overflow-hidden rounded-2xl border border-as-pink-200/60 bg-card text-foreground shadow-xl md:block"
              {...panelMotion}
            >
              <div className="max-h-[80vh] overflow-y-auto">
                <ChecklistPanel
                  title={title}
                  completedCount={completedCount}
                  total={total}
                  status={status}
                  prefersReducedMotion={prefersReducedMotion}
                  onGo={handleGo}
                  onSkip={(id) => { void skipStep(id); }}
                />
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default OnboardingChecklist;
